/**
 * Event-level tableBookingPriceHike — exact backend shape.
 * { enabled, hikeType: PERCENT|FIXED, hikeValue, scope: ALL|SECTION|TABLE, areaIds[], tables[{areaId,tableId}] }
 */

export const HIKE_TYPE = {
  PERCENT: "PERCENT",
  FIXED: "FIXED",
};

export const HIKE_SCOPE = {
  ALL: "ALL",
  SECTION: "SECTION",
  TABLE: "TABLE",
};

export function emptyTableBookingPriceHike() {
  return {
    enabled: false,
    hikeType: HIKE_TYPE.PERCENT,
    hikeValue: 0,
    scope: HIKE_SCOPE.ALL,
    areaIds: [],
    tables: [],
  };
}

export function pickTableBookingPriceHike(event) {
  const nested =
    event?.tableBookingPriceHike ??
    event?.tableBooking?.priceHike ??
    event?.tableBooking?.tableBookingPriceHike ??
    null;
  if (!nested || typeof nested !== "object") {
    return emptyTableBookingPriceHike();
  }
  const hikeType =
    String(nested.hikeType ?? nested.hike_type ?? HIKE_TYPE.PERCENT).toUpperCase() ===
    HIKE_TYPE.FIXED
      ? HIKE_TYPE.FIXED
      : HIKE_TYPE.PERCENT;
  const scopeRaw = String(nested.scope ?? HIKE_SCOPE.ALL).toUpperCase();
  const scope = [HIKE_SCOPE.ALL, HIKE_SCOPE.SECTION, HIKE_SCOPE.TABLE].includes(
    scopeRaw
  )
    ? scopeRaw
    : HIKE_SCOPE.ALL;
  const areaIds = Array.isArray(nested.areaIds)
    ? nested.areaIds.map((id) => String(id).trim()).filter(Boolean)
    : [];
  const tables = Array.isArray(nested.tables)
    ? nested.tables
        .map((row) => ({
          areaId: String(row?.areaId ?? row?.area_id ?? "").trim(),
          tableId: String(row?.tableId ?? row?.table_id ?? "").trim(),
        }))
        .filter((row) => row.areaId && row.tableId)
    : [];
  const hikeValue = Number(nested.hikeValue ?? nested.hike_value ?? 0);
  return {
    enabled: Boolean(nested.enabled),
    hikeType,
    hikeValue: Number.isFinite(hikeValue) ? hikeValue : 0,
    scope,
    areaIds,
    tables,
  };
}

export function applyHikeToUnitPrice(basePrice, hike) {
  const base = Number(basePrice) || 0;
  if (!hike?.enabled) return base;
  const value = Number(hike.hikeValue) || 0;
  if (hike.hikeType === HIKE_TYPE.FIXED) {
    return Math.max(0, base + value);
  }
  return Math.max(0, Math.round(base * (1 + value / 100)));
}

export function tableMatchesHikeScope(areaId, tableId, hike) {
  if (!hike?.enabled) return false;
  const a = String(areaId ?? "").trim();
  const t = String(tableId ?? "").trim();
  if (hike.scope === HIKE_SCOPE.ALL) return true;
  if (hike.scope === HIKE_SCOPE.SECTION) {
    return (hike.areaIds || []).includes(a);
  }
  return (hike.tables || []).some(
    (row) =>
      String(row.areaId) === a && String(row.tableId) === t
  );
}

export function isLiveTableBookable(liveKey) {
  return !liveKey || liveKey === "available";
}

export function validateTableBookingPriceHike(hike, t) {
  if (!hike?.enabled) return null;
  const value = Number(hike.hikeValue);
  if (!Number.isFinite(value) || value < 0) {
    return t("EVENT_HIKE_ERR_VALUE");
  }
  if (hike.hikeType === HIKE_TYPE.PERCENT && value > 500) {
    return t("EVENT_HIKE_ERR_PERCENT_CAP");
  }
  if (hike.scope === HIKE_SCOPE.SECTION && !(hike.areaIds || []).length) {
    return t("EVENT_HIKE_ERR_SECTIONS");
  }
  if (hike.scope === HIKE_SCOPE.TABLE && !(hike.tables || []).length) {
    return t("EVENT_HIKE_ERR_TABLES");
  }
  return null;
}

export function buildTableBookingPriceHikePayload(hike) {
  if (!hike?.enabled) {
    return { tableBookingPriceHike: { enabled: false } };
  }
  const payload = {
    enabled: true,
    hikeType:
      hike.hikeType === HIKE_TYPE.FIXED ? HIKE_TYPE.FIXED : HIKE_TYPE.PERCENT,
    hikeValue: Number(hike.hikeValue) || 0,
    scope: hike.scope || HIKE_SCOPE.ALL,
  };
  if (payload.scope === HIKE_SCOPE.SECTION) {
    payload.areaIds = [...(hike.areaIds || [])];
  }
  if (payload.scope === HIKE_SCOPE.TABLE) {
    payload.tables = (hike.tables || []).map((row) => ({
      areaId: String(row.areaId),
      tableId: String(row.tableId),
    }));
  }
  return { tableBookingPriceHike: payload };
}
